/**
 * Export functions registry
 *
 * Provides format options and exporter dispatch.
 * Individual exporters are in separate files.
 */
import type { EnrichedCollectionCard, Folder } from '@/types/card';

import { exportDragonShield } from './dragonshield';
import { exportMoxfield } from './moxfield';
import { exportMtga } from './mtga';
import { exportMtgCraft } from './mtgcraft';

// ============================================================================
// Types
// ============================================================================

export type ExportFormat = 'dragonshield' | 'moxfield' | 'mtga' | 'mtgcraft';

// ============================================================================
// Export Format Options
// ============================================================================

export const EXPORT_FORMATS: { value: ExportFormat; label: string; extension: string }[] = [
  { value: 'dragonshield', label: 'Dragon Shield CSV', extension: 'csv' },
  { value: 'moxfield', label: 'Moxfield CSV', extension: 'csv' },
  { value: 'mtga', label: 'MTGA Text', extension: 'txt' },
  { value: 'mtgcraft', label: 'MtgCraft Backup (JSON)', extension: 'json' },
];

// ============================================================================
// Export Dispatch
// ============================================================================

/**
 * Export collection to specified format
 */
export function exportCollection(
  format: ExportFormat,
  cards: EnrichedCollectionCard[],
  folders: Folder[],
  folderNames: Map<string, string>,
): string {
  switch (format) {
    case 'dragonshield':
      return exportDragonShield(cards, folderNames);
    case 'moxfield':
      return exportMoxfield(cards);
    case 'mtga':
      return exportMtga(cards);
    case 'mtgcraft':
      return exportMtgCraft(cards, folders);
    default:
      throw new Error(`Unknown export format: ${format}`);
  }
}

// ============================================================================
// Export Utilities
// ============================================================================

/**
 * Download exported data as file
 */
export function downloadExport(data: string, filename: string, mimeType: string): void {
  const blob = new Blob([data], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Copy exported data to clipboard
 */
export async function copyToClipboard(data: string): Promise<void> {
  await navigator.clipboard.writeText(data);
}
